import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { LogIn } from "lucide-react";

export default function TeacherLogin() {
  const { user, login, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (user && (user.is_teacher || user.is_admin)) {
      navigate(params.get("next") || "/teacher");
    }
  }, [user, loading, navigate, params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(form.email, form.password);
      if (!u.is_teacher && !u.is_admin) {
        logout();
        toast.error("This account isn't set up as a teacher. Ask your coordinator for access.");
        return;
      }
      toast.success(`Welcome, ${u.name?.split(" ")[0] || "teacher"}.`);
      navigate(params.get("next") || "/teacher");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24" data-testid="teacher-login-page">
      <div className="overline mb-3">Teacher sign-in</div>
      <h1 className="font-serif-display text-4xl tracking-tight" style={{ color: "var(--ink)" }}>Sign in</h1>
      <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
        Use the email + password your coordinator gave you.
      </p>
      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <div>
          <label className="block text-xs mb-2 overline">Email</label>
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="teacher-login-email" className="w-full bg-white border px-4 py-3 outline-none focus:border-[var(--terracotta)]" style={{ borderColor: "var(--border-strong)", color: "var(--ink)" }} />
        </div>
        <div>
          <label className="block text-xs mb-2 overline">Password</label>
          <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="teacher-login-password" className="w-full bg-white border px-4 py-3 outline-none focus:border-[var(--terracotta)]" style={{ borderColor: "var(--border-strong)", color: "var(--ink)" }} />
        </div>
        <button type="submit" disabled={busy} data-testid="teacher-login-submit" className="btn-primary w-full disabled:opacity-50">
          <LogIn size={14} /> {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <p className="mt-6 text-xs" style={{ color: "var(--muted)" }}>
        Forgot your password? Your coordinator can reset it from the CRM.
      </p>
    </div>
  );
}
